import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AppBar,
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import CropFreeIcon from '@mui/icons-material/CropFreeOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Divider from '@mui/material/Divider';

import type { Scene as SceneType, CameraMode } from '@/types/scene.types';

import { THOR_COLORS } from '@/theme/thorTheme';
import Header from '@/components/Header';
// Import the main function from app_clean.ts
import { main, app, getJsonFromUrl } from '../test/app_clean';

import { SimulationOverlay } from '@/components/SimulationOverlay';
import { SceneNavigation } from '@/components/SceneNavigation';
import Loading from '@/components/Loading';
import { fetchScenes } from '@/api/Scene';

export const Test = () => {
  // const { sceneIndex } = useParams<{ sceneIndex: string }>();
  const { slug } = useParams<{ slug: string }>();
  const containerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  const [scene, setScene] = useState<SceneType | undefined>(undefined);
  const [cameraMode, setCameraMode] = useState<CameraMode>('top-down');
  const [hadChanceToReadAllInstructions, setHadChanceToReadAllInstructions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [_isPlaying, setIsPlaying] = useState(false);
  const [withDebugTools, setWithDebugTools] = useState(false);
  const [_isSceneClean, setIsSceneClean] = useState(false);
  const [overlays, setOverlays] = useState<any[]>([]);

  useEffect(() => {
    setIsLoading(true);
    fetchScenes()
      .then((allScenes) => {
        // setScene(new Scene());
        const featuredScene = allScenes.find((scene) => scene.slug === slug);
        console.log(`--------- ${featuredScene?.actionsJsonFile}`);
        setScene(featuredScene);

        const getParams: any = getJsonFromUrl(window.location.search);

        console.log(`--------------- getParams ${getParams}`);
        console.log(getParams);
        let debugMode = false;

        if ('debug' in getParams && getParams.debug.toLowerCase() === 'true') {
          setWithDebugTools(true);
          debugMode = true;
        }

        // Only initialize once
        if (initializedRef.current) {
          return;
        }

        // Ensure the container exists
        if (!containerRef.current) {
          return;
        }

        // Create a canvas element with the expected ID before calling main()
        // The MujocoApp constructor will use this canvas if it exists
        let canvas = document.getElementById('mujoco-canvas') as HTMLCanvasElement;
        if (!canvas) {
          canvas = document.createElement('canvas');
          canvas.id = 'mujoco-canvas';
          canvas.style.width = '100%';
          canvas.style.height = '100%';
          canvas.style.display = 'block';
          // canvas.style.zIndex = '9999';
          containerRef.current.appendChild(canvas);
        }

        // Initialize the app
        initializedRef.current = true;
        main(featuredScene?.actionsJsonFile, containerRef.current)
          .catch((error) => {
            console.error('Failed to initialize MuJoCo test:', error);
            initializedRef.current = false; // Allow retry on error
          })
          .then(() => {
            console.log('---------------- loaded');
            setIsLoading(false);
            setIsSceneClean(true);
            app.thirdPersonPanControlsEnable = debugMode;
            app.debugMode = debugMode;

            app.overlayCallback = setOverlays;
            app.reactPlayCallback = handleReplayClick;

            app.onResize();
          });
      })
      .catch((err: unknown) => {
        console.error('Failed to fetch scenes:', err);
      });

    // Cleanup on unmount
    return () => {
      // The app_clean.ts code handles cleanup through its dispose method
      // We'll let it handle its own cleanup
    };
  }, [slug]);

  const handleReplayClick = async (useCurrentCameraPosition: boolean = false) => {
    // Call the replay function from the MujocoApp
    console.log(`+++++++++ Replay button clicked ${app.sceneCleanState}`);

    if (app) {
      // Reset the entire scene (closes oven doors, resets object positions, etc.)
      if (!app.sceneCleanState) {
        let cameraPos = useCurrentCameraPosition ? app.lastCameraPositon : null;
        await app.reset(cameraPos);
        // Reset robot to initial position and start playback
      }
      if (app.actionIndex !== 0) {
        app.resetPlayback();
      }
      // Resume playback (workaround for toggle logic in playActions)
      app.playActions();
      setIsSceneClean(false);
      setIsPlaying(true);
      console.log('🔄 Scene reset and action playback restarted');
      let actionFinishedCheckInterval: NodeJS.Timeout | null;
      actionFinishedCheckInterval = setInterval(function () {
        if (app.isPlaybackActionFinished() && actionFinishedCheckInterval) {
          clearInterval(actionFinishedCheckInterval);
          setIsPlaying(false);
        }
      }, 200);
    }
  };

  const setActiveAction = async (index: number) => {
    if (app) {
      console.log(`------------ current index ${app.activeActionIndex} new index ${index}`);

      if (app.activeActionIndex !== index) {
        console.log(`------------ call setActiveActionIndex`);
        app.setActiveActionIndex(index);
        // TODO: optimization that could lead to problems remove if if problems
        // Insead of reseting the scene which is expensive, if scene was just reset and is clean
        // instead just teleport the object and reset playback indices

        console.log(
          `------------ handleViewClick on diff indices sceneCleanState  ${app.sceneCleanState}}`
        );
        if (!app.sceneCleanState) {
          await app.reset();
          setIsPlaying(false);
        } else {
          app.calculateDynamicCameraPosition();
        }
        setIsSceneClean(true);
        app.resetPlayback();
        app.setCameraMode('3rd-person');
      } else {
        if (!app.sceneCleanState && app.actionFinished) {
          await app.reset();
          setIsSceneClean(true);
        } else {
          // app.calculateDynamicCameraPosition();
        }
        app.setCameraMode('3rd-person');
        // app.setInteractEvents();
      }

      // app.setCameraMode('3rd-person');
      // app.setCameraMode('3rd-person');

      // Reset robot to initial position and start playback
    }
    app.needToCastHoverRay = true;

    if (!hadChanceToReadAllInstructions) {
      console.log('+++++++++++++++++ setHadChanceToReadAllInstructions true');
      setHadChanceToReadAllInstructions(true);
    }
  };

  const handlePreviousAction = async () => {
    console.log('Previous action button clicked');
    if (app) {
      let totalActions = app.getTotalActionNumber();
      let prevIndex = (app.activeActionIndex - 1 + totalActions) % totalActions;
      await setActiveAction(prevIndex);
    }
  };
  const handleNextAction = async () => {
    console.log('Next action button clicked');
    if (app) {
      let totalActions = app.getTotalActionNumber();
      let nextIndex = (app.activeActionIndex + 1) % totalActions;
      await setActiveAction(nextIndex);
    }
  };

  const handleViewClick = async (index: number) => {
    // e.stopPropagation();
    console.log(`View button clicked - switching to 3rd-person mode`);
    setCameraMode('3rd-person');
    await setActiveAction(index);
  };
  const onlyCanvas = false;

  const objectOverlays = overlays.map((x: any, index: number) => {
    return !onlyCanvas ? (
      <SimulationOverlay
        onViewClick={async () => handleViewClick(index)}
        key={index}
        show={cameraMode === 'top-down'}
        x={x.screenPosition.x * 100}
        y={x.screenPosition.y * 100}
        object_name={x.alias ? x.alias : x.name}
      />
    ) : null;
  });

  const cycleActionsButtonSize: number = 56;
  const cycleActionsButtonFontSize: number = 42;
  // const playButtonSize: number = 72; // 56 og
  // const playButtonFontSize: number = 50; // 32 og

  return (
    <Box
      // sx={{
      //   width: '100vw',
      //   height: '100vh',
      //   position: 'relative',
      //   overflow: 'hidden',
      // }}
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {!onlyCanvas ? (
        <>
          <Loading isLoading={isLoading} />
          <Header />{' '}
          <AppBar
            position="static"
            color="default"
            sx={{
              backgroundColor: 'rgba(10, 50, 53, 0.9)',
              '--Paper-overlay': 'none',
              backgroundImage: 'none',
              height: 'auto',
            }}
          >
            <Toolbar
              sx={{
                flexWrap: 'wrap',
                gap: 1,
                minHeight: { xs: '64px', sm: '64px' },
                height: 'auto',
                py: { xs: 1, sm: 0 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Link
                  href="/environment"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '1.2rem',
                    color: THOR_COLORS.lightGreen,
                    '&:hover': {
                      color: THOR_COLORS.lightGreen,
                      opacity: 0.8,
                    },
                  }}
                >
                  <ArrowBackIcon />
                  Back to environments
                </Link>
                <Divider
                  orientation="vertical"
                  sx={{ mx: 2, height: '24px', display: { xs: 'none', sm: 'block' } }}
                />
                <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                  {scene?.name}
                </Typography>
              </Box>

              {!isLoading ? (
                <Box
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                  }}
                >
                  <ToggleButtonGroup
                    value={cameraMode}
                    exclusive
                    onChange={(_, newMode) => {
                      if (newMode !== null) {
                        setCameraMode(newMode);
                        app?.setCameraMode(newMode);
                        // TODO bug with autorotate
                        app?.setCameraMode(newMode);
                        if (!hadChanceToReadAllInstructions) {
                          console.log('+++++++++++++++++ setHadChanceToReadAllInstructions true');
                          setHadChanceToReadAllInstructions(true);
                        }
                      }
                    }}
                    orientation="horizontal"
                    sx={{
                      height: { xs: 'auto', sm: '36px' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      width: { xs: '100%', sm: 'auto' },
                      '& .MuiToggleButton-root': {
                        whiteSpace: 'nowrap',
                        width: { xs: '100%', sm: 'auto' },
                        minHeight: { xs: '36px', sm: 'auto' },
                      },
                    }}
                  >
                    <ToggleButton value="top-down" aria-label="top-down">
                      <CropFreeIcon sx={{ mr: 1 }} />
                      Top-down
                    </ToggleButton>

                    <ToggleButton value="3rd-person" aria-label="3rd-person">
                      <RemoveRedEyeIcon sx={{ mr: 1 }} />
                      3rd person
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ) : null}
            </Toolbar>
          </AppBar>
        </>
      ) : null}
      {/* Canvas container */}
      <Box
        ref={containerRef}
        id="canvas-container"
        // sx={{
        //   width: '100%',
        //   height: '100%',
        //   position: 'relative',
        // }}
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {objectOverlays}

        {/* {isLoading ? null : <SceneNavigation cameraMode={cameraMode} />} */}
      </Box>

      {/* Button container - matching the prototype's layout */}
      {withDebugTools ? (
        <Box
          id="button-container"
          sx={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '10px',
            zIndex: 1000,
          }}
        >
          <button id="left-button">&lt;</button>
          <button id="print-camera">Print Cam Params</button>
          <button id="pause-button">Pause</button>
          <button id="reset-button">Reset</button>
          <button id="contact-button">Show Contacts</button>
          <button id="play-button">Play</button>
          <button id="stop-button">Stop</button>
          <button id="right-button">&gt;</button>
        </Box>
      ) : null}

      {!onlyCanvas ? (
        <Box
          sx={{
            // position: 'absolute',
            position: 'fixed',
            left: '2%',
            right: '2%',
            bottom: 24,

            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
            justifyContent: 'space-between',

            gap: 1.5,
            flexWrap: 'wrap',
            pointerEvents: 'auto',
          }}
        >
          {isLoading || onlyCanvas ? null : (
            <Box
              sx={
                {
                  // minWidth: 280,     //wrapping stuff
                  // flexShrink: 0,
                }
              }
            >
              <SceneNavigation
                cameraMode={cameraMode}
                startExpanded={true}
                collapseAfterInitialWaitSeconds={-1}
                collapseAfterClickSeconds={20}
                startInitialCollapseTimerOnTrue={hadChanceToReadAllInstructions}
              />
            </Box>
          )}
          {cameraMode !== 'top-down' ? (
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                gap: 1.5,
                minWidth: 120,
                flexShrink: 0,
                marginLeft: 'auto', // keeps it right-aligned on wide screens
              }}
            >
              <Tooltip title="Previous action" placement="top">
                <IconButton
                  onClick={handlePreviousAction}
                  sx={{
                    backgroundColor: 'rgba(240, 82, 156, 0.6)',
                    color: THOR_COLORS.cream,
                    transform: 'scale(1)',
                    '&:hover': {
                      transform: 'scale(1.2)',
                      backgroundColor: 'rgba(240, 82, 156, 0.9)',
                    },
                    width: cycleActionsButtonSize,
                    height: cycleActionsButtonSize,
                  }}
                >
                  <NavigateBeforeIcon sx={{ fontSize: cycleActionsButtonFontSize }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Next action" placement="top">
                <IconButton
                  onClick={handleNextAction}
                  sx={{
                    backgroundColor: 'rgba(240, 82, 156, 0.6)',
                    color: THOR_COLORS.cream,
                    transform: 'scale(1)',
                    '&:hover': {
                      transform: 'scale(1.2)',
                      backgroundColor: 'rgba(240, 82, 156, 0.9)',
                    },
                    width: cycleActionsButtonSize,
                    height: cycleActionsButtonSize,
                  }}
                >
                  <NavigateNextIcon sx={{ fontSize: cycleActionsButtonFontSize }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {/* {isLoading || onlyCanvas ? null : <SceneNavigation cameraMode={cameraMode} startExpanded={true} collapseAfterInitialWaitSeconds={-1} collapseAfterClickSeconds={20} startInitialCollapseTimerOnTrue={hadChanceToReadAllInstructions}/>}   */}
    </Box>
  );
};
